'use client'
import * as React from 'react'
import {
  TabContentDefaultWrapper,
  tabContentWrapperId,
} from '../../../tab-content/wrapper'
import { Button, ButtonLink } from '~/sotano-kit/primitives/button'
import { Bookmark } from '~/app/(dash)/[team-slug]/header/bookmark-action'
import {
  useIsMemberOrHigher,
  useIsExternalVisitor,
  useIsGuestInOthersBranch,
} from '~/hooks-data/use-user-role'
import {
  ArrowLeftIcon,
  BookmarkFilledIcon,
  BookmarkIcon,
  CaretSortIcon,
} from '@radix-ui/react-icons'
import { ForkIcon } from '~/app/(dash)/[team-slug]/(team)/components/repositories/repository/fork-icon'
import { ForkDialog } from '~/app/(dash)/[team-slug]/header/fork-dialog'
import { Contributors } from '~/app/(dash)/[team-slug]/(team)/components/repositories/repository/contributors'
import { clsx } from 'clsx'
import { useSelectedTeamAndRepo } from '~/hooks-data/use-selected-team-and-repo'
import {
  PublicRepo,
  PublicTeam,
} from '@basehub/data/replicache/mutators/_public-types'
import { useToggleState } from '~/hooks/use-toggle-state'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { AIModelingInput } from '../../../explore/components/blocks/empty-state/ai-modeling'
import { SchemaAssistantReturnType } from '~/app/api/ai/schema-assistant/client'
import { ENABLE_SCHEMA_ASSISTANT } from '~/app/api/ai/schema-assistant/_constants'

import Link from 'next/link'
import { EditorStoreProvider } from '../../../explore/components/editor-store'
import { BreadcrumbStickyContainer } from '../../../explore/components/tabs/dashboard-breadcrumb'
import { Breadcrumb, BreadcrumbItem } from '~/sotano-kit/primitives/breadcrumb'
import RelevantLinks from '../../../explore/components/tabs/dashboard-breadcrumb/relevant-links'
import {
  useBranchOrCommitSegment,
  useSelectedBranchOrCommitQuery,
} from '~/lib/commits/use-selected-branch-or-commit'
import {
  Branch,
  Commit,
  Repository,
  Team,
  isCommit,
  isPlayground,
} from '@basehub/data/replicache/index'
import { useBranches } from '~/hooks-data/use-branches'
import { BranchBadge } from '../badge'
import { useBranchMergeDiff } from '../../_hooks/use-merge-diff'
import { BranchMergeDiff } from '../ahead'
import { BranchSwitcher } from '~/sotano-kit/primitives/branch-switcher'
import { PopoverTrigger } from '~/sotano-kit/primitives/popover'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { calculateConflicts } from '../../_hooks/use-active-conflicts-wizard'
import { BranchMergeIcon, MergeIcon } from '~/components/icons/branch-icon'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import { BranchOverview } from '../overview'
import { ArchiveIcon } from '~/components/icons/archive'
import { useTabsStore } from '../../../explore/components/tabs/store'
import { Skeleton } from '~/sotano-kit/primitives/skeleton'
import { useTimeDiff } from '~/hooks/use-time-diff'
import { useUserData } from '~/hooks-data/use-user-data'
import { BaseHubAvatar } from '~/components/basehub-avatar'
import { useForkOrigin } from '~/hooks-data/use-fork-origin'
import { getPlaygroundDisplayName } from '@basehub/data/replicache/playground/utils'

const AI_PROMPT_LIST = [
  'build a blog for my space travel company',
  'build an about page',
  'build a changelog for my SaaS company',
  'build a developer documentation',
]

const RANDOM_ARRAY_INDEX_FOR_AI_PROMPTS = Math.floor(
  Math.random() * AI_PROMPT_LIST.length
)

const [RESOLVED_PROMPT] = AI_PROMPT_LIST.splice(
  RANDOM_ARRAY_INDEX_FOR_AI_PROMPTS,
  1
)

export const Timeline = ({
  onboardingGuides,
}: {
  onboardingGuides: React.ReactNode
}) => {
  const branchOrCommit = useSelectedBranchOrCommitQuery().data
  const isMemberOrHigher = useIsMemberOrHigher()
  const { data } = useSelectedTeamAndRepo()
  const repository = data?.repo
  const team = data?.team
  const [container, setContainer] = React.useState<HTMLDivElement | null>(null)

  React.useEffect(() => {
    const containerEl = document.getElementById(
      tabContentWrapperId
    ) as HTMLDivElement | null
    if (containerEl) {
      setContainer(containerEl)
    }
  }, [])

  if (!repository || !team || !branchOrCommit) return null

  return (
    <>
      {isCommit(branchOrCommit) ? (
        <CommitBreadcrumb commit={branchOrCommit} />
      ) : (
        <BranchBreadcrumb branch={branchOrCommit} />
      )}
      <EditorStoreProvider>
        <TabContentDefaultWrapper fullHeight>
          <div className="pb-14 flex-1 flex flex-col">
            <div className="flex justify-between items-start gap-x-4">
              <div className="flex-1 pl-1 -ml-1 mb-4 flex items-center">
                <h2 className="text-2xl font-medium leading-[1.3] flex-1 text-dark-bright">
                  {isCommit(branchOrCommit)
                    ? branchOrCommit.message
                    : getPlaygroundDisplayName(branchOrCommit)}
                </h2>

                {!isCommit(branchOrCommit) && (
                  <BranchActions
                    branch={branchOrCommit}
                    {...(branchOrCommit.isDefault && { repository, team })}
                  />
                )}
              </div>
            </div>

            <BranchOrCommitMetadata
              container={container}
              team={team}
              repository={repository}
              branchOrCommit={branchOrCommit}
            />

            {ENABLE_SCHEMA_ASSISTANT && isMemberOrHigher && (
              <div className="mb-10">
                <READMEAIInput accordionsId="readme-tabs" />
              </div>
            )}

            {onboardingGuides}

            {!isCommit(branchOrCommit) && (
              <BranchOverview branch={branchOrCommit} />
            )}
          </div>
        </TabContentDefaultWrapper>
      </EditorStoreProvider>
    </>
  )
}

const CommitBreadcrumb = ({ commit }: { commit: Commit }) => {
  return (
    <BreadcrumbStickyContainer>
      <Breadcrumb>
        <BreadcrumbItem active>{commit.id}</BreadcrumbItem>
      </Breadcrumb>
      <RelevantLinks />
    </BreadcrumbStickyContainer>
  )
}

const BranchBreadcrumb = ({ branch }: { branch: Branch }) => {
  const sourceBranch = useBranches().data?.find(
    (b) => b.id === branch.sourceBranchId
  )

  return (
    <BreadcrumbStickyContainer>
      <Breadcrumb>
        <BreadcrumbItem active>
          {branch.isDefault ? (
            'Default branch'
          ) : isPlayground(branch) ? (
            'Content request'
          ) : (
            <>
              {sourceBranch?.name ?? ''}&nbsp;&nbsp;{'<-'}&nbsp;&nbsp;
              {branch.name}
            </>
          )}
        </BreadcrumbItem>
      </Breadcrumb>
      <RelevantLinks />
    </BreadcrumbStickyContainer>
  )
}

/* -------------------------------------------------------------------------------------------------
 * Branch actions
 * -----------------------------------------------------------------------------------------------*/

type ActionsProps = { branch: Branch } & (DefautBranchActionsProps | object)

const BranchActions = ({ branch, ...props }: ActionsProps) => {
  if (branch.isDefault && 'repository' in props) {
    return <DefaultBranchActions {...props} />
  }

  return <FeatureBranchActions branch={branch} {...props} />
}

type DefautBranchActionsProps = {
  repository: Repository | PublicRepo
  team: Team | PublicTeam
  forkPrimaryButton?: boolean
}

export const DefaultBranchActions = ({
  repository,
  team,
}: DefautBranchActionsProps) => {
  const forkToggleState = useToggleState()

  return (
    <div className="flex items-center gap-2">
      <Bookmark intent="asChild" team={team} repository={repository}>
        {({ isBookmarked }) => {
          const amountOfBookmarks = repository.stats?.bookmarks ?? 0

          return (
            <Button
              intent="secondary"
              icon={
                isBookmarked ? (
                  <BookmarkFilledIcon height={14} width={14} />
                ) : (
                  <BookmarkIcon height={14} width={14} />
                )
              }
              className="!pr-1.5"
              iconSide="left"
            >
              Bookmark{isBookmarked ? 'ed' : ''}
              <span className="sr-only">bookmark count</span>
              <span className="bg-dark-elevated rounded-[100px] leading-tight px-2 whitespace-nowrap h-max text-xs">
                {amountOfBookmarks}
              </span>
            </Button>
          )
        }}
      </Bookmark>

      <ForkDialog
        toggleState={forkToggleState}
        team={team}
        repository={repository}
        queryParam="fork"
        hideTrigger={false}
      >
        <Button
          intent="secondary"
          icon={<ForkIcon height={14} width={14} />}
          className="!pr-1.5"
          iconSide="left"
        >
          Fork
          <span className="sr-only">fork count</span>
          <span className="bg-dark-elevated rounded-[100px] leading-tight px-2 whitespace-nowrap h-max text-xs">
            {repository.stats?.forks ?? 0}
          </span>
        </Button>
      </ForkDialog>
    </div>
  )
}

export const FeatureBranchActions = ({
  branch,
}: Pick<ActionsProps, 'branch'>) => {
  const { encodedSegment } = useBranchOrCommitSegment()
  const { teamSlug, repoSlug } = useDashboardSlugs()
  const { diffResult } = useBranchMergeDiff({
    branch,
  })

  return (
    <div className="flex gap-2">
      {Boolean(diffResult.diff?.changesCount) && (
        <ButtonLink
          href={`/${teamSlug}/${repoSlug}/branch/${encodedSegment}`}
          intent="secondary"
        >
          See {diffResult.diff?.changesCount} change
          {(diffResult.diff?.changesCount ?? 0) > 1 ? 's' : ''}
        </ButtonLink>
      )}
      <BranchStatusBadge branch={branch} />
    </div>
  )
}

const BranchStatusBadge = ({ branch }: { branch: Branch }) => {
  const baseClassName =
    'text-xs font-semibold h-7 pl-2 pr-2.5 rounded-full flex items-center gap-0.5 whitespace-nowrap'
  const { status, cr, diffResult } = useBranchMergeDiff({
    branch,
  })

  let icon: React.ReactNode
  let title: React.ReactNode
  let className = ''

  if (status === 'archived' && Boolean(branch.archivedAt)) {
    icon = <ArchiveIcon />
    title = <>Archived</>
    className = 'text-dark-muted bg-dark-shade bg-dark-cr-open/15'
  } else if (status === 'merged') {
    icon = <MergeIcon />
    title = <>Merged</>
    className = 'text-dark-cr-merged bg-dark-cr-merged/15'
  } else if (status === 'conflicts') {
    const { unresolvedCount } = calculateConflicts({
      conflictResolutions: cr?.conflictResolutions,
      changesTree: diffResult.diff?.changesTree,
    })
    icon = (
      <svg
        width="16"
        height="17"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M14.934 8.5a6.933 6.933 0 1 1-13.867 0 6.933 6.933 0 0 1 13.867 0ZM7.2 5.034a.8.8 0 1 1 1.6 0v4.8a.8.8 0 1 1-1.6 0v-4.8Zm.8 6.133a.8.8 0 1 0 0 1.6.8.8 0 0 0 0-1.6Z"
          fill="currentColor"
        />
      </svg>
    )
    title =
      unresolvedCount === 0 ? (
        <>Waiting for update</>
      ) : (
        <>
          Has {unresolvedCount} conflict{unresolvedCount === 1 && 's'}
        </>
      )
    className = 'text-warning bg-warning/15'
  } else {
    icon = <BranchMergeIcon />
    title = <>Open</>
    className = 'text-dark-cr-open bg-dark-cr-open/15'
  }

  return (
    <div className={clsx(baseClassName, className)}>
      {icon}
      <span className="ml-0.5 font-medium">{title}</span>
    </div>
  )
}

/* -------------------------------------------------------------------------------------------------
 * Branch or commit metadata
 * -----------------------------------------------------------------------------------------------*/

type BranchOrCommitMetadata = {
  team: Team | PublicTeam
  repository: Repository | PublicRepo
  container: React.ComponentProps<typeof Contributors>['collisionBoundary']
  branchOrCommit: Branch | Commit
}

const BranchOrCommitMetadata = ({
  branchOrCommit,
  ...props
}: BranchOrCommitMetadata) => {
  if (isCommit(branchOrCommit)) {
    return <CommitMetadata commit={branchOrCommit} {...props} />
  }

  return <BranchMetadata branch={branchOrCommit} {...props} />
}

/* -------------------------------------------------------------------------------------------------
 * Commit metadata
 * -----------------------------------------------------------------------------------------------*/

const CommitMetadata = (
  props: Omit<BranchOrCommitMetadata, 'branchOrCommit'> & { commit: Commit }
) => {
  const timeDiff = useTimeDiff(new Date(props.commit.createdAt))
  const user = useUserData(props.commit.authorId)
  const userFullName = user.data?.firstName
    ? user.data?.firstName + ' ' + (user.data?.lastName ?? '')
    : (user.data?.username ?? '')

  return (
    <div className="flex items-center mb-10 text-dark-faint h-6">
      {user.isLoading ? (
        <Skeleton height={24} width={24} className="rounded-full" />
      ) : (
        <>
          <BaseHubAvatar withBorder id={user.data?.id} name={userFullName} />
          <p className="text-xs leading-4 text-dark-dim ml-1.5">
            {userFullName}
          </p>
        </>
      )}
      <span className="mx-2">/</span>
      <BranchBadge className="text-xs px-2 h-6" variant="muted">
        {timeDiff}
      </BranchBadge>
    </div>
  )
}

/* -------------------------------------------------------------------------------------------------
 * Branch metadata
 * -----------------------------------------------------------------------------------------------*/

type BranchMetadataProps = Omit<BranchOrCommitMetadata, 'branchOrCommit'> & {
  branch: Branch
}

const BranchMetadata = (props: BranchMetadataProps) => {
  const contributors = React.useMemo(() => {
    if (props.branch.isDefault || !props.branch.authorId) {
      return props.branch.contributors
    }
    return [
      ...new Set([props.branch.authorId, ...(props.branch.contributors ?? [])]),
    ]
  }, [props.branch])

  return (
    <div className="flex items-center mb-10 text-dark-faint h-6">
      {contributors?.length ? (
        <>
          <Contributors
            className="pointer-events-auto"
            collisionBoundary={props.container}
            contributorsIds={contributors}
            team={props.team}
            repository={props.repository}
            avatarSize={24}
            showedContributors={10}
          />
          <span className="mx-2">/</span>
        </>
      ) : null}

      {props.branch.isDefault ? (
        <DefaultBranchMetadata {...props} />
      ) : (
        <FeatureBranchMetadata {...props} />
      )}
    </div>
  )
}

const DefaultBranchMetadata = ({
  branch,
  team,
  repository,
}: BranchMetadataProps) => {
  const addTab = useTabsStore((s) => s.addTab)
  const encodedSegment = useBranchOrCommitSegment().encodedSegment
  const { repoPage } = useDashboardSlugs()
  const { data: forkOrigin, isLoading } = useForkOrigin(repository, team)
  const activeBranches = useBranches({
    baseBranchId: branch.id,
    archived: false,
  })
  const activeBranchesBadge = activeBranches.data
    ? `${activeBranches.data.length || 'No'} open branch${
        activeBranches.data.length === 1 ? '' : 'es'
      }`
    : ''

  return (
    <>
      <BranchBadge className="text-xs px-2 h-6 max-w-48" variant="muted">
        <span className="truncate">Default branch</span>
      </BranchBadge>
      {Boolean(activeBranchesBadge) && (
        <>
          <span className="mx-2">/</span>
          <BranchBadge
            className="text-xs px-2 h-6 max-w-48"
            href={{
              pathname: `/${team.slug}/${repository.slug}/${repoPage}/${encodedSegment}/branches:active`,
            }}
            onClick={(e) => {
              if (e.ctrlKey || e.metaKey) return

              addTab({
                key: `branches:active`,
                partiallyOpen: true,
              })
            }}
            variant="muted"
          >
            <span className="truncate">{activeBranchesBadge}</span>
          </BranchBadge>
        </>
      )}
      {repository.forkStatus && (
        <div className="text-xs text-dark-muted truncate contents">
          {forkOrigin ? (
            <>
              <span className="mx-2 inline">/</span>
              <span>
                Forked from{' '}
                <Link
                  href={`/${forkOrigin.teamSlug}/${forkOrigin.repoSlug}`}
                  className="text-dark-control-base hover:underline whitespace-nowrap truncate"
                >
                  {forkOrigin.teamSlug}/{forkOrigin.repoSlug}
                </Link>
              </span>
            </>
          ) : (
            <>
              {isLoading && (
                <>
                  <span className="mx-2">/</span>
                  <Skeleton height={24} width={96} />
                </>
              )}
            </>
          )}
        </div>
      )}
    </>
  )
}

const FeatureBranchMetadata = ({ branch }: BranchMetadataProps) => {
  const replicache = useRepoReplicache()?.data?.replicache
  const branchesQuery = useBranches({ status: 'open' })
  const crId = branch.contentRequestIds?.[0]
  const { behind, ahead, baseBranch, cr } = useBranchMergeDiff({ branch })
  const isGuestInOthersBranch = useIsGuestInOthersBranch()

  const handleSelectBranch = React.useCallback(
    (branch: Branch) => {
      if (!crId) return

      replicache?.mutate.updateContentRequest({
        id: crId,
        baseBranchId: branch.id,
      })
    },
    [replicache, crId]
  )

  if (!baseBranch) {
    return (
      <>
        <Skeleton height={24} width={80} />
        <ArrowLeftIcon className="mx-1" />
        <Skeleton height={24} width={80} />
        <span className="mx-2">/</span>
        <BranchMergeDiff behind={behind} ahead={ahead} className="h-6" />
      </>
    )
  }

  return (
    <>
      {isGuestInOthersBranch || isPlayground(branch) ? (
        <BranchBadge variant="highlight" className="py-0.5 px-2 text-xs">
          {baseBranch.name}
        </BranchBadge>
      ) : (
        <BranchSwitcher
          trigger={
            <PopoverTrigger asChild>
              <button className="font-medium text-dark-accent text-xs hover:bg-dark-accent/25 bg-dark-accent/15 pl-2 pr-1 h-6 rounded leading-none flex items-center cursor-default focus-visible:outline outline-1 outline-offset-2 outline-dark-control-base">
                <span className="whitespace-nowrap mr-0.5 max-w-[300px] text-ellipsis overflow-hidden">
                  {baseBranch.name}
                </span>
                <CaretSortIcon />
              </button>
            </PopoverTrigger>
          }
          activeBranchOrCommit={baseBranch}
          branches={
            branchesQuery.data?.filter((b) => b.id !== cr?.headBranchId) ?? []
          }
          header={{ title: 'Change base branch' }}
          inputPlaceholder="Find a branch..."
          onSelect={handleSelectBranch}
          hideBranchManager
        />
      )}
      <ArrowLeftIcon className="mx-1" />
      <BranchBadge variant="highlight" className="text-xs py-0.5 px-2">
        {getPlaygroundDisplayName(branch)}
      </BranchBadge>

      <span className="mx-2">/</span>
      <BranchMergeDiff behind={behind} ahead={ahead} className="h-6" />
    </>
  )
}

const READMEAIInput = ({ accordionsId }: { accordionsId: string }) => {
  const timeoutRef = React.useRef<NodeJS.Timeout>(undefined)
  const [status, setStatus] =
    React.useState<SchemaAssistantReturnType['status']>('idle')

  // accordions ref
  const developerGuideRef = React.useRef<HTMLDivElement>(null)
  const isVisitor = useIsExternalVisitor()

  const aiInputRef = React.useRef<HTMLInputElement>(null)
  const workingTree = useWorkingTreeStore((s) => s.treeRef)
  const rootBlock = workingTree?.current?.root

  const [isHovering, setIsHovering] = React.useState(false)

  React.useEffect(() => {
    if (status !== 'idle') setIsHovering(false)
  }, [status])

  const queryParamsIncludeDevelopmentGuide = new URLSearchParams(
    window.location.search
  ).has('developer-guide')

  React.useEffect(() => {
    if (!developerGuideRef.current) return

    if (!queryParamsIncludeDevelopmentGuide) return

    const timeout = setTimeout(() => {
      developerGuideRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, 0)

    return () => {
      clearTimeout(timeout)
    }
  }, [queryParamsIncludeDevelopmentGuide])

  React.useEffect(() => {
    const accordionEl = document.querySelector<HTMLDivElement>(
      '#' + accordionsId
    )
    if (!accordionEl || isVisitor) return
    if (isHovering) {
      accordionEl.style.transform = `translateY(calc((38px * ${AI_PROMPT_LIST.length})))`

      return () => {
        accordionEl.style.transform = 'none'
      }
    }
  }, [accordionsId, isHovering, isVisitor])

  return (
    <div className="relative z-10" style={{ height: '82px' }}>
      <AIModelingInput
        submitButtonClassName={clsx(
          isHovering && '-rotate-90',
          'transition-transform duration-200 ease-out'
        )}
        path={rootBlock?.value?.id ?? 'example-id'}
        ref={aiInputRef}
        mode="create-document"
        placeholder="Create content with AI..."
        onStatusChange={(status) => setStatus(status)}
      />
      {/* Prompts */}
      <Button
        unstyled
        focusStyled
        onMouseEnter={() => {
          if (status !== 'idle') {
            setIsHovering(false)
            if (timeoutRef.current) clearTimeout(timeoutRef.current)
            return
          }

          timeoutRef.current = setTimeout(
            () => setIsHovering(true),
            isHovering ? 0 : 1000
          )
        }}
        onMouseLeave={() => {
          if (status !== 'idle') return

          if (timeoutRef.current) clearTimeout(timeoutRef.current)
          setIsHovering(false)
        }}
        style={{ zIndex: AI_PROMPT_LIST.length + 1 }}
        className={clsx(
          status === 'loading'
            ? 'cursor-wait'
            : 'hover:text-dark-dim cursor-default',
          '!-outline-offset-1 p-3 pb-1.5 text-dark-muted h-[38px] truncate border border-dark-border-solid bg-dark-shade rounded-b-lg -translate-y-2 text-xs w-full text-left relative'
        )}
        onClick={() => {
          if (aiInputRef.current === null || status !== 'idle') return
          aiInputRef.current.value = RESOLVED_PROMPT ?? ''
          aiInputRef.current.select()
        }}
      >
        Ask something like “{RESOLVED_PROMPT}”
      </Button>
      <div
        className={clsx('-mt-6', !isHovering && 'pointer-events-none')}
        onMouseEnter={() => {
          if (status !== 'idle') {
            setIsHovering(false)
            if (timeoutRef.current) clearTimeout(timeoutRef.current)
            return
          }

          timeoutRef.current = setTimeout(
            () => setIsHovering(true),
            isHovering ? 0 : 1000
          )
        }}
        onMouseLeave={() => {
          if (status !== 'idle') return

          if (timeoutRef.current) clearTimeout(timeoutRef.current)
          setIsHovering(false)
        }}
      >
        {AI_PROMPT_LIST.map((prompt, index) => {
          return (
            <Button
              key={index}
              unstyled
              focusStyled
              tabIndex={isHovering ? 0 : -1}
              style={{
                zIndex: AI_PROMPT_LIST.length - index - 1,
                transform: !isHovering
                  ? // 40 = height of each, + 1 because we have one less prompt in this map (RESOLVED_PROMPT) is the one that is always shown
                    `translateY(calc(-38px * ${index + 1}))`
                  : `translateY(calc(-8px * ${index - 1}))`,
              }}
              className="transition-transform duration-150 !-outline-offset-1 ease-out cursor-default p-3 pb-1.5 truncate text-dark-muted hover:text-dark-dim border border-dark-border-solid rounded-t-none bg-dark-shade rounded-b-lg text-xs w-full relative text-left"
              onClick={() => {
                if (aiInputRef.current === null || status !== 'idle') {
                  return
                }
                aiInputRef.current.value = prompt ?? ''
                aiInputRef.current.select()
              }}
            >
              Ask something like “{prompt}”
            </Button>
          )
        })}
      </div>
    </div>
  )
}
